import { StandardEvents } from '@shopify/events';

/**
 * MAISON ARPENT — la carte de livraison estimée.
 *
 * Les dates sont calculées ICI et pas en Liquid : Shopify met les pages en
 * cache, une date rendue au serveur serait fausse dès le lendemain. On compte
 * en jours OUVRÉS — samedi et dimanche ne comptent pas — et on rend la date
 * dans la langue du navigateur, qui est aussi celle de la boutique.
 *
 * Le tube et le poids, eux, viennent d'une table Liquid : ils dépendent de la
 * variante, pas de la date. Le JS choisit une ligne, il ne remesure rien.
 *
 * ⚠️ Les jours fériés ne sont pas gérés. Une fourchette de trois jours ouvrés
 * les absorbe presque toujours ; en absorber la totalité demanderait un
 * calendrier à tenir à jour, pour un gain qui ne se voit pas.
 *
 * Balisage : `blocks/arpent-livraison.liquid`
 */
class Livraison extends HTMLElement {
  /** @type {{diametre:number, variantes:{id:number,tube:string,poids:string}[]}|null} */
  #donnees = null;
  /** @type {Element | null} */
  #contexte = null;
  #ecoutes = new AbortController();

  connectedCallback() {
    this.#lireDonnees();
    this.#dater();

    this.#contexte = this.closest('[id*="ProductInformation-"], [id*="QuickAdd-"], product-card');
    this.#contexte?.addEventListener(StandardEvents.productSelect, this.#surVariante, {
      signal: this.#ecoutes.signal,
    });
  }

  disconnectedCallback() {
    this.#ecoutes.abort();
    this.#contexte = null;
  }

  #lireDonnees() {
    const contenu = this.querySelector('[data-liv="donnees"]')?.textContent;
    if (!contenu) return;

    try {
      const brut = JSON.parse(contenu);
      if (Array.isArray(brut?.variantes)) this.#donnees = brut;
    } catch (erreur) {
      console.warn('[arpent-livraison] table des tubes illisible', erreur);
    }
  }

  /**
   * Ajoute `nombre` jours OUVRÉS à aujourd'hui.
   * @param {number} nombre
   */
  #ouvres(nombre) {
    const jour = new Date();
    let comptes = 0;

    while (comptes < nombre) {
      jour.setDate(jour.getDate() + 1);
      // 0 = dimanche, 6 = samedi. Le modulo 6 vaut 0 pour ces deux-là.
      if (jour.getDay() % 6 !== 0) comptes += 1;
    }

    return jour;
  }

  #dater() {
    const format = { weekday: 'long', day: 'numeric', month: 'long' };
    const langue = document.documentElement.lang || 'fr-FR';
    const dire = (date) => date.toLocaleDateString(langue, format);

    const expedition = Number(this.dataset.joursExpedition || 2);
    const min = Number(this.dataset.joursMin || 4);
    const max = Number(this.dataset.joursMax || 7);

    // Le connecteur (« Chez vous entre le », « Expédition le ») est écrit ICI et
    // pas en Liquid : sans JS, la phrase de repli doit se tenir seule. Rendre
    // « Chez vous entre le » côté serveur donnait « Chez vous entre le 10 et
    // 20 jours » à un robot qui n'exécute pas le script.
    const titre = this.dataset.titreDelai || '';
    const detail = this.dataset.detailDelai || '';

    const fourchette = this.querySelector('[data-liv="fourchette"]');
    if (fourchette)
      fourchette.textContent =
        `${titre} ${dire(this.#ouvres(min))} et le ${dire(this.#ouvres(max))}`.trim();

    const jourExpedition = this.querySelector('[data-liv="expedition"]');
    if (jourExpedition) jourExpedition.textContent = `${detail} ${dire(this.#ouvres(expedition))}`.trim();
  }

  /** @param {any} evenement */
  #surVariante = (evenement) => {
    evenement.promise
      ?.then(({ detail }) => {
        const id = detail?.resource?.id;
        const donnees = this.#donnees;
        if (!id || !donnees) return;

        const ligne = donnees.variantes.find((v) => String(v.id) === String(id));
        if (!ligne) return;

        const tube = this.querySelector('[data-liv="tube"]');
        if (tube) tube.textContent = ligne.tube;

        const poids = this.querySelector('[data-liv="poids"]');
        if (poids) poids.textContent = ligne.poids;
      })
      .catch((erreur) => {
        if (erreur?.name !== 'AbortError') console.warn('[arpent-livraison]', erreur);
      });
  };
}

if (!customElements.get('arpent-livraison')) {
  customElements.define('arpent-livraison', Livraison);
}

/**
 * La ligne de disponibilité, au-dessus du bouton d'ajout. Même sujet, même
 * fichier — elle tient en vingt lignes et ne mérite pas un module à elle.
 *
 * Elle ne recalcule rien : au changement de variante elle se fait REMPLACER par
 * celle que le serveur vient de rendre. C'est le geste d'Horizon pour son propre
 * `product-inventory`, et c'est le seul qui garantisse que la phrase affichée
 * soit celle du Liquid, réglages compris.
 *
 * Balisage : `blocks/arpent-disponibilite.liquid`
 */
class Disponibilite extends HTMLElement {
  /** @type {Element | null} */
  #contexte = null;
  #ecoutes = new AbortController();

  connectedCallback() {
    this.#contexte = this.closest('[id*="ProductInformation-"], [id*="QuickAdd-"], product-card');
    this.#contexte?.addEventListener(StandardEvents.productSelect, this.#surVariante, {
      signal: this.#ecoutes.signal,
    });
  }

  disconnectedCallback() {
    this.#ecoutes.abort();
    this.#contexte = null;
  }

  /** @param {any} evenement */
  #surVariante = (evenement) => {
    if (!(evenement.target instanceof Element) || evenement.target.closest('product-card')) return;

    evenement.promise
      ?.then(({ detail }) => {
        const neuve = detail?.html?.querySelector('arpent-dispo');
        if (neuve) this.replaceWith(neuve);
      })
      .catch((erreur) => {
        if (erreur?.name !== 'AbortError') console.warn('[arpent-dispo]', erreur);
      });
  };
}

if (!customElements.get('arpent-dispo')) {
  customElements.define('arpent-dispo', Disponibilite);
}
